import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { uploadOptimizedImage } from "@/lib/image-upload";
import {
  getIntroPageContent,
  upsertIntroContent,
  type Pillar,
} from "@/lib/intro.functions";
import { FieldOverridesButton } from "@/components/admin/overrides/FieldOverridesButton";
import { ChevronLeft, Save, Upload } from "lucide-react";

const PAGE_KEY = "intro";

const uploadIntroImage = (folder: string) => async (f: File) => {
  const { url } = await uploadOptimizedImage({
    file: f,
    bucket: "slide-media",
    folder: `intro/${folder}`,
  });
  return url;
};

const uploadLoungeImage = uploadIntroImage("lounge");
const uploadPillarImage = uploadIntroImage("pillars");

export const Route = createFileRoute("/_authenticated/admin/intro")({
  head: () => ({ meta: [{ title: "Intro CMS" }, { name: "robots", content: "noindex" }] }),
  component: IntroAdmin,
});

type FormState = {
  featured_label: string;
  rotating_words_text: string;
  intro_headline: string;
  intro_body: string;
  pillars_heading: string;
  pillars: Pillar[];
  closer_intro_text: string;
  closer_text: string;
  lounge_image_url: string;
  reviews_kicker: string;
  reviews_subtitle: string;
};

function IntroAdmin() {
  const fetchContent = useServerFn(getIntroPageContent);
  const upsert = useServerFn(upsertIntroContent);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const loungeFileRef = useRef<HTMLInputElement | null>(null);
  const [uploadingLounge, setUploadingLounge] = useState(false);
  const [uploadingPillarIdx, setUploadingPillarIdx] = useState<number | null>(null);

  const { data: content } = useQuery({
    queryKey: ["admin", "intro-content"],
    queryFn: () => fetchContent(),
  });

  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (!content) return;
    setForm({
      featured_label: content.featured_label ?? "",
      rotating_words_text: (content.rotating_words ?? []).join("\n"),
      intro_headline: content.intro_headline ?? "",
      intro_body: content.intro_body ?? "",
      pillars_heading: content.pillars_heading ?? "",
      pillars: (content.pillars ?? []).map((p) => ({ ...p })),
      closer_intro_text: content.closer_intro_text ?? "",
      closer_text: content.closer_text ?? "",
      lounge_image_url: content.lounge_image_url ?? "",
      reviews_kicker: content.reviews_kicker ?? "",
      reviews_subtitle: content.reviews_subtitle ?? "",
    });
  }, [content]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin", "intro-content"] });
    qc.invalidateQueries({ queryKey: ["intro-content"] });
  };

  const persist = async (next: FormState | null) => {
    if (!next) return;
    await upsert({
      data: {
        id: content?.id,
        featured_label: next.featured_label || null,
        rotating_words: next.rotating_words_text
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        intro_headline: next.intro_headline || null,
        intro_body: next.intro_body || null,
        pillars_heading: next.pillars_heading || null,
        pillars: next.pillars.map((p) => ({
          num: p.num || "",
          title: p.title || "",
          body: p.body || "",
          image_url: p.image_url || "",
        })),
        closer_intro_text: next.closer_intro_text || null,
        closer_text: next.closer_text || null,
        lounge_image_url: next.lounge_image_url || null,
        reviews_kicker: next.reviews_kicker || null,
        reviews_subtitle: next.reviews_subtitle || null,
      },
    });
    invalidate();
  };

  const save = useMutation({
    mutationFn: () => persist(form),
    onSuccess: () => toast.success("Saved"),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const handleLoungeUpload = async (f: File) => {
    if (!form) return;
    setUploadingLounge(true);
    try {
      const url = await uploadLoungeImage(f);
      const next = { ...form, lounge_image_url: url };
      setForm(next);
      await persist(next);
      toast.success("Image saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploadingLounge(false);
      if (loungeFileRef.current) loungeFileRef.current.value = "";
    }
  };

  const updatePillar = (idx: number, patch: Partial<Pillar>) => {
    if (!form) return;
    const next = {
      ...form,
      pillars: form.pillars.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    };
    setForm(next);
  };

  const handlePillarUpload = async (idx: number, f: File) => {
    if (!form) return;
    setUploadingPillarIdx(idx);
    try {
      const url = await uploadPillarImage(f);
      const next = {
        ...form,
        pillars: form.pillars.map((p, i) => (i === idx ? { ...p, image_url: url } : p)),
      };
      setForm(next);
      await persist(next);
      toast.success("Pillar image saved");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploadingPillarIdx(null);
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-background text-foreground">
      <Toaster richColors closeButton position="top-right" />
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Link to="/admin" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <ChevronLeft className="w-3 h-3" /> Back to admin
            </Link>
            <h1 className="text-2xl font-serif mt-1">Intro CMS</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Edit the homepage rotating words, intro copy, pillar list, closing line, and reviews headings.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate({ to: "/" })}>View site</Button>
        </div>

        {!form ? (
          <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading…
          </div>
        ) : (
          <>
            <Panel title="Hero (rotating words)" description="The words that cycle behind 'Rediscover' on the homepage video.">
              <Field
                label="Rotating words — one per line"
                overrideKey="rotating_words"
                overrideType="longtext"
              >
                <Textarea
                  rows={7}
                  value={form.rotating_words_text}
                  onChange={(e) => setForm({ ...form, rotating_words_text: e.target.value })}
                  placeholder={"each other\nstillness\ndark skies"}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Words cycle in order. Keep them short so the layout stays stable.
                </p>
              </Field>
            </Panel>

            <Panel title="Featured-in section" description="The label above the press logo marquee.">
              <Field label="Label" overrideKey="featured_label" overrideType="text">
                <Input
                  value={form.featured_label}
                  onChange={(e) => setForm({ ...form, featured_label: e.target.value })}
                  placeholder="FEATURED IN"
                />
              </Field>
            </Panel>

            <Panel title="Intro statement" description="The centered headline and supporting paragraph that opens the pillars section.">
              <Field label="Headline" overrideKey="intro_headline" overrideType="text">
                <Input
                  value={form.intro_headline}
                  onChange={(e) => setForm({ ...form, intro_headline: e.target.value })}
                  placeholder="Perfect for couples who want a truly unique romantic getaway"
                />
              </Field>
              <Field label="Body" overrideKey="intro_body" overrideType="longtext">
                <Textarea
                  rows={4}
                  value={form.intro_body}
                  onChange={(e) => setForm({ ...form, intro_body: e.target.value })}
                />
              </Field>
            </Panel>

            <Panel title="Pillars" description="The sticky-heading list and the four scrolling cards beneath it.">
              <Field label="Sticky heading" overrideKey="pillars_heading" overrideType="text">
                <Input
                  value={form.pillars_heading}
                  onChange={(e) => setForm({ ...form, pillars_heading: e.target.value })}
                  placeholder="How we built Backland to be different"
                />
              </Field>

              <div className="space-y-4">
                {form.pillars.map((p, i) => {
                  const n = i + 1;
                  return (
                  <div key={i} className="rounded-md border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Pillar {n}
                      </Label>
                    </div>
                    <div className="grid grid-cols-[80px_1fr] gap-3">
                      <div>
                        <Label className="text-xs">Number</Label>
                        <Input
                          value={p.num}
                          onChange={(e) => updatePillar(i, { num: e.target.value })}
                          placeholder="01"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs">Title</Label>
                          <FieldOverridesButton
                            pageKey={PAGE_KEY}
                            fieldKey={`pillar_${n}_title`}
                            label={`Pillar ${n} — Title`}
                            type="text"
                          />
                        </div>
                        <Input
                          value={p.title}
                          onChange={(e) => updatePillar(i, { title: e.target.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">Body</Label>
                        <FieldOverridesButton
                          pageKey={PAGE_KEY}
                          fieldKey={`pillar_${n}_body`}
                          label={`Pillar ${n} — Body`}
                          type="longtext"
                        />
                      </div>
                      <Textarea
                        rows={3}
                        value={p.body}
                        onChange={(e) => updatePillar(i, { body: e.target.value })}
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">Background image</Label>
                        <FieldOverridesButton
                          pageKey={PAGE_KEY}
                          fieldKey={`pillar_${n}_image`}
                          label={`Pillar ${n} — Image`}
                          type="image"
                          onUploadImage={uploadPillarImage}
                        />
                      </div>
                      <div className="flex items-start gap-3 mt-1">
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-32 h-20 object-cover rounded" />
                        ) : (
                          <div className="w-32 h-20 rounded bg-muted" />
                        )}
                        <div className="flex-1 space-y-2">
                          <Input
                            value={p.image_url}
                            onChange={(e) => updatePillar(i, { image_url: e.target.value })}
                            placeholder="https://… or upload"
                          />
                          <label className="inline-flex">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handlePillarUpload(i, f);
                                e.target.value = "";
                              }}
                            />
                            <Button asChild type="button" size="sm" variant="outline" disabled={uploadingPillarIdx === i}>
                              <span>
                                <Upload className="w-4 h-4 mr-1" />
                                {uploadingPillarIdx === i ? "Uploading…" : "Upload"}
                              </span>
                            </Button>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

            </Panel>

            <Panel title="Closing line" description="The two-line closer that sticks beneath the pillars list as the cards scroll past.">
              <Field label="Intro line (fades out)" overrideKey="closer_intro_text" overrideType="text">
                <Input
                  value={form.closer_intro_text}
                  onChange={(e) => setForm({ ...form, closer_intro_text: e.target.value })}
                  placeholder="There are many places to stay near the Grand Canyon…"
                />
              </Field>
              <Field label="Closer line (gold italic)" overrideKey="closer_text" overrideType="text">
                <Input
                  value={form.closer_text}
                  onChange={(e) => setForm({ ...form, closer_text: e.target.value })}
                  placeholder="but there is nowhere else like Backland."
                />
              </Field>
              <Field label="Lounge image (shown after the closer)" overrideKey="lounge_image" overrideType="image" onUploadImage={uploadLoungeImage}>
                <div className="flex items-start gap-3">
                  {form.lounge_image_url ? (
                    <img src={form.lounge_image_url} alt="" className="w-32 h-32 object-cover rounded" />
                  ) : (
                    <div className="w-32 h-32 rounded bg-muted" />
                  )}
                  <div className="flex-1 space-y-2">
                    <Input
                      value={form.lounge_image_url}
                      onChange={(e) => setForm({ ...form, lounge_image_url: e.target.value })}
                      placeholder="https://… or upload"
                    />
                    <input
                      ref={loungeFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleLoungeUpload(f);
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => loungeFileRef.current?.click()}
                      disabled={uploadingLounge}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      {uploadingLounge ? "Uploading…" : "Upload"}
                    </Button>
                  </div>
                </div>
              </Field>
            </Panel>

            <Panel title="Reviews section" description="Headings above the embedded reviews widget.">
              <Field label="Kicker" overrideKey="reviews_kicker" overrideType="longtext">
                <Textarea
                  rows={2}
                  placeholder={"ARIZONA'S TOP RATED\nNATURE RESORT"}
                  value={form.reviews_kicker}
                  onChange={(e) => setForm({ ...form, reviews_kicker: e.target.value })}
                />
              </Field>
              <Field label="Subtitle" overrideKey="reviews_subtitle" overrideType="text">
                <Input
                  value={form.reviews_subtitle}
                  onChange={(e) => setForm({ ...form, reviews_subtitle: e.target.value })}
                />
              </Field>
            </Panel>

            <div className="flex justify-end">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {save.isPending ? "Saving…" : "Save all"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Panel({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-5 space-y-5">
      <div>
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({
  label,
  overrideKey,
  overrideType,
  onUploadImage,
  children,
}: {
  label: string;
  overrideKey: string;
  overrideType: "text" | "longtext" | "image";
  onUploadImage?: (f: File) => Promise<string>;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{label}</Label>
        <FieldOverridesButton
          pageKey={PAGE_KEY}
          fieldKey={overrideKey}
          label={label}
          type={overrideType}
          onUploadImage={onUploadImage}
        />
      </div>
      {children}
    </div>
  );
}
